import { Cell, CellFormula, CellValueType, Dimension, Element } from '../../services/rating-matrix.service';

export interface ConfigurationReportElement {
  dimensionId: string;
  dimensionTitle: string;
  elementId: string;
  elementTitle: string;
  sourceElement?: Element;
}

export interface ConfigurationReportRow {
  cellId: string;
  value: number;
  valueType: CellValueType;
  formula: CellFormula | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  elements: ConfigurationReportElement[];
}

export interface ConfigurationReportTable {
  depth: number;
  dimensionIds: string[];
  headers: string[];
  rows: ConfigurationReportRow[];
}

export interface ConfigurationReportSection {
  depth: number;
  tables: ConfigurationReportTable[];
}

export interface ConfigurationReport {
  sections: ConfigurationReportSection[];
}

export function buildConfigurationReport(
  cells: Cell[],
  elementById: Map<string, Element>,
  dimensionById: Map<string, Dimension>
): ConfigurationReport {
  const tableMap = new Map<string, ConfigurationReportTable>();

  for (const cell of cells) {
    const elements = resolveCellElements(cell, elementById, dimensionById);
    if (elements.length === 0) {
      continue;
    }

    const sortedElements = sortReportElements(elements);
    const depth = sortedElements.length;
    const dimensionIds = sortedElements.map(element => element.dimensionId);
    const tableKey = dimensionIds.join('|');

    let table = tableMap.get(tableKey);
    if (!table) {
      table = {
        depth,
        dimensionIds,
        headers: sortedElements.map(element => element.dimensionTitle),
        rows: [],
      };
      tableMap.set(tableKey, table);
    }

    table.rows.push({
      cellId: cell.id,
      value: Number(cell.value),
      valueType: cell.valueType ?? 'FIXED',
      formula: cell.formula ?? null,
      effectiveFrom: cell.effectiveFrom ?? null,
      effectiveTo: cell.effectiveTo ?? null,
      elements: sortedElements,
    });
  }

  const tables = Array.from(tableMap.values()).map(table => ({
    ...table,
    rows: [...table.rows].sort(compareReportRows),
  }));

  tables.sort(compareReportTables);

  const sections: ConfigurationReportSection[] = [];
  const tablesByDepth = new Map<number, ConfigurationReportTable[]>();

  for (const table of tables) {
    const depthTables = tablesByDepth.get(table.depth) ?? [];
    depthTables.push(table);
    tablesByDepth.set(table.depth, depthTables);
  }

  const depths = Array.from(tablesByDepth.keys()).sort((left, right) => left - right);
  for (const depth of depths) {
    sections.push({
      depth,
      tables: tablesByDepth.get(depth) ?? [],
    });
  }

  return { sections };
}

function resolveCellElements(
  cell: Cell,
  elementById: Map<string, Element>,
  dimensionById: Map<string, Dimension>
): ConfigurationReportElement[] {
  const elements: ConfigurationReportElement[] = [];
  const seenDimensionIds = new Set<string>();

  for (const cellElement of cell.elements ?? []) {
    const element = cellElement.element ?? elementById.get(cellElement.elementId);
    if (!element) {
      continue;
    }

    if (seenDimensionIds.has(element.dimensionId)) {
      continue;
    }

    seenDimensionIds.add(element.dimensionId);
    const dimension = element.dimension ?? dimensionById.get(element.dimensionId);
    elements.push({
      dimensionId: element.dimensionId,
      dimensionTitle: dimension?.title ?? element.dimensionId,
      elementId: element.id,
      elementTitle: element.title,
      sourceElement: element,
    });
  }

  return elements;
}

export function sortReportElements(elements: ConfigurationReportElement[]): ConfigurationReportElement[] {
  return [...elements].sort(compareReportElements);
}

export function compareReportElements(left: ConfigurationReportElement, right: ConfigurationReportElement): number {
  const dimensionTitleComparison = left.dimensionTitle.localeCompare(right.dimensionTitle);
  if (dimensionTitleComparison !== 0) {
    return dimensionTitleComparison;
  }

  const leftSortLabel = getReportElementSortLabel(left);
  const rightSortLabel = getReportElementSortLabel(right);
  const elementTitleComparison = leftSortLabel.localeCompare(rightSortLabel, undefined, {
    sensitivity: 'base',
  });
  if (elementTitleComparison !== 0) {
    return elementTitleComparison;
  }

  return left.elementId.localeCompare(right.elementId);
}

function getReportElementSortLabel(element: ConfigurationReportElement): string {
  // Nested elements (Make/Model, sub-distributors, ...) sort by their full hierarchy label
  if (!element.sourceElement?.parent) {
    return element.elementTitle;
  }

  return buildHierarchyLabel(element);
}

function buildHierarchyLabel(element: ConfigurationReportElement): string {
  if (!element.sourceElement) {
    return element.elementTitle;
  }

  const segments: string[] = [];
  const visited = new Set<string>();
  let cursor: Element | null | undefined = element.sourceElement;

  while (cursor && !visited.has(cursor.id)) {
    visited.add(cursor.id);
    segments.unshift(cursor.title);
    cursor = cursor.parent ?? undefined;
  }

  return segments.length > 0 ? segments.join(' - ') : element.elementTitle;
}

function compareReportRows(left: ConfigurationReportRow, right: ConfigurationReportRow): number {
  const length = Math.max(left.elements.length, right.elements.length);
  for (let index = 0; index < length; index += 1) {
    const leftElement = left.elements[index];
    const rightElement = right.elements[index];

    if (!leftElement && rightElement) {
      return -1;
    }
    if (leftElement && !rightElement) {
      return 1;
    }
    if (!leftElement || !rightElement) {
      continue;
    }

    const comparison = compareReportElements(leftElement, rightElement);
    if (comparison !== 0) {
      return comparison;
    }
  }

  if (left.value !== right.value) {
    return left.value - right.value;
  }

  return left.cellId.localeCompare(right.cellId);
}

function compareReportTables(left: ConfigurationReportTable, right: ConfigurationReportTable): number {
  if (left.depth !== right.depth) {
    return left.depth - right.depth;
  }

  const length = Math.max(left.headers.length, right.headers.length);
  for (let index = 0; index < length; index += 1) {
    const headerComparison = (left.headers[index] ?? '').localeCompare(right.headers[index] ?? '');
    if (headerComparison !== 0) {
      return headerComparison;
    }
  }

  return left.dimensionIds.join('|').localeCompare(right.dimensionIds.join('|'));
}
